import { useEffect, useState } from 'react';

import { FieldLabel, NumericInput } from 'fluxo-ui';

import type { ComponentEvent } from 'fluxo-ui';

interface TimePickerProps {
    value?: number; // Total seconds
    onChange?: (seconds: number) => void;
    label?: string;
    hint?: string;
    required?: boolean;
    disabled?: boolean;
    error?: string;
    maxMinutes?: number;
    maxSeconds?: number;
}

const TimePicker: React.FC<TimePickerProps> = ({
    value = 0,
    onChange,
    label = 'Time',
    hint,
    required = false,
    disabled = false,
    error,
    maxMinutes = 180,
    maxSeconds = 59,
}) => {
    const [minutes, setMinutes] = useState<number>(Math.floor(value / 60));
    const [seconds, setSeconds] = useState<number>(value % 60);

    useEffect(() => {
        setMinutes(Math.floor(value / 60));
        setSeconds(value % 60);
    }, [value]);

    const handleMinutesChange = (e: ComponentEvent<number | undefined>) => {
        const newMinutes = Math.max(0, Math.min(maxMinutes, e.value || 0));
        setMinutes(newMinutes);
        const totalSeconds = newMinutes * 60 + seconds;
        onChange?.(totalSeconds);
    };

    const handleSecondsChange = (e: ComponentEvent<number | undefined>) => {
        const newSeconds = Math.max(0, Math.min(maxSeconds, e.value || 0));
        setSeconds(newSeconds);
        const totalSeconds = minutes * 60 + newSeconds;
        onChange?.(totalSeconds);
    };

    const formatTime = () => {
        if (minutes === 0 && seconds === 0) return '0s';
        const parts = [];
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0) parts.push(`${seconds}s`);
        return parts.join(' ');
    };

    return (
        <div className="space-y-2">
            {label && (
                <div>
                    <label className="block text-sm font-medium text-slate-900 dark:text-white mb-1">
                        {label}
                        {required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {hint && <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">{hint}</p>}
                </div>
            )}
            <div className="flex items-center gap-3">
                <div className="flex-1">
                    <FieldLabel label="Minutes">
                        <NumericInput
                            value={minutes}
                            onChange={handleMinutesChange}
                            min={0}
                            max={maxMinutes}
                            disabled={disabled}
                            placeholder="0"
                        />
                    </FieldLabel>
                </div>
                <span className="text-lg font-semibold text-slate-500 dark:text-slate-400 mt-6">:</span>
                <div className="flex-1">
                    <FieldLabel label="Seconds">
                        <NumericInput
                            value={seconds}
                            onChange={handleSecondsChange}
                            min={0}
                            max={maxSeconds}
                            disabled={disabled}
                            placeholder="0"
                        />
                    </FieldLabel>
                </div>
            </div>
            <div className="flex items-center justify-between">
                {error ? (
                    <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
                ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Total: {formatTime()}</p>
                )}
            </div>
        </div>
    );
};

export default TimePicker;
